import type { FetchStatus, QueryStatus } from '@tanstack/react-query'
import type { CSSProperties, Dispatch, ReactNode, SetStateAction } from 'react'

import cx from 'classnames'
import { Fragment, useCallback, useState } from 'react'
import { MdChevronRight, MdFilterAlt } from 'react-icons/md'
import { animated, config, useSpring } from 'react-spring'
import { useDebounce } from 'react-use'

import BarsIcon from '../../../assets/svgs/bars.svg?component'
import { ErrorPlaceHolder, NoDataPlaceHolder } from '../../PatientFolder/behavioral/components'
import { PlainButton } from '../../shared/Button'
import * as Card from '../../shared/card'
import { Pagination as PaginationComponent } from '../components/pagination'

import { ContainerMeasureProvider } from './action-button-dropdown'
import { FiltersDropdown, FiltersDropdownWrapper } from './filters-dropdown'
import { LoadingIndicator } from './loading-indicator'
import styles from './table.module.css'

export interface IdBearer {
  id: string | number
}

export interface Item extends
  IdBearer,
  Record<string, unknown> { }

export interface Column<T extends Item> {
  name: keyof T | Exclude<string, keyof T>
  header: React.ReactNode
  isSortable?: boolean
  initialSortDirection?: SortDirection
  isMergeable?: boolean
  getValue?: (item: T, index: number) => React.ReactNode
  gridArea?: string
  style?: CSSProperties
  headerStyle?: CSSProperties
  isAction?: boolean
}

export type SortDirection = 'asc' | 'desc'

const sortDirectionTooltipsMapper: Record<SortDirection, string> = {
  desc: 'Sort DESC',
  asc: 'Sort ASC'
}

export interface Sort<T extends Item> {
  field: keyof T
  direction: SortDirection
}

export interface Pagination {
  totalFiltered?: number
  totalUnfiltered?: number
  page: number
  pageSize: number
  extra?: React.ReactNode
}

export interface FilterWithOptions {
  name: string
  value: Array<string | number | boolean>
  options: Array<{
    value: string | number | boolean
    label?: React.ReactNode
    searchBy?: string
    helperText?: string
  }>
}

interface CustomFilterRenderProps {
  onChange: (value: Array<string | number | boolean>) => void
  onClose: () => void
}

export interface CustomFilter {
  name: string
  render: (props: CustomFilterRenderProps) => React.ReactNode
  isActive?: boolean
  isDisabled?: boolean
}

export type Filter = FilterWithOptions | CustomFilter

interface BaseProps<T extends Item> {
  columns: Array<Column<T>>
  data: T[]
  header?: React.ReactNode
  expandable?: boolean
  error?: Error
  queryStatus?: QueryStatus
  fetchStatus?: FetchStatus
  loadingIndicator?: boolean
  onRefetch?: () => Promise<unknown> | unknown
  disableMinHeight?: boolean
  expandingComponent?: (
    item: T,
    state: string | number | null,
    setState: Dispatch<SetStateAction<string | number | null>>
  ) => ReactNode
  noDataPlaceholder?: string
  wrapperStyle?: CSSProperties
  children?: React.ReactNode
  hideBorders?: boolean
  onClick?: (item: T) => boolean | undefined
}

interface WithSort<T extends Item> {
  sort: Sort<T>
  onSortChange: (field: keyof T, direction: SortDirection) => void
}

interface WithoutSort {
  sort?: undefined
  onSortChange?: undefined
}

type SortProps<T extends Item> =
    | WithSort<T>
    | WithoutSort

interface WithPagination {
  pagination: Pagination
  onPageChange: (page: number) => void
  onPageSizeChange: (pageSize: number) => void
}

interface WithoutPagination {
  pagination?: undefined
  onPageChange?: undefined
  onPageSizeChange?: undefined
}

type PaginationProps =
    | WithPagination
    | WithoutPagination

interface WithFilters<T extends Item> {
  filters: Filter[]
  forceClearFilters?: boolean
  onFilterChange: (field: keyof T, values: Array<string | number | boolean>) => void
  onFiltersClear: () => void
}

interface WithoutFilters {
  filters?: undefined
  forceClearFilters?: undefined
  onFilterChange?: undefined
  onFiltersClear?: () => void
}

type FilterProps<T extends Item> =
  | WithFilters<T>
  | WithoutFilters

type Props<T extends Item> = BaseProps<T> & FilterProps<T> & PaginationProps & SortProps<T>

export function Table<T extends Item> ({
  header,
  children,
  columns,
  data,
  sort,
  pagination,
  filters,
  error,
  queryStatus,
  fetchStatus,
  forceClearFilters = false,
  loadingIndicator = true,
  onSortChange,
  onPageChange,
  onPageSizeChange,
  onFilterChange,
  onFiltersClear,
  onRefetch,
  disableMinHeight,
  expandingComponent: renderExpandingComponent,
  noDataPlaceholder,
  wrapperStyle,
  expandable,
  onClick,
  hideBorders = true
}: Props<T>): JSX.Element | null {
  let [ currentlyOpenFilter, setCurrentlyOpenFilter ] = useState<keyof T>()
  let [ debouncedQueryStatus, setDebouncedQueryStatus ] = useState(queryStatus)
  let [ debouncedFetchStatus, setDebouncedFetchStatus ] = useState(fetchStatus)
  let [ expanded, setExpanded ] = useState<string | number | null>(null)
  let isEmpty = debouncedQueryStatus === 'success' && !data.length

  let canClearFiltersForMoreResults =
    typeof pagination?.totalUnfiltered === 'number' &&
    typeof pagination?.totalFiltered === 'number' &&
    (pagination.totalUnfiltered > pagination.totalFiltered) &&
    typeof onFiltersClear === 'function'

  let severityFilter = filters?.find(filter => filter.name === 'severity')

  let isSpecialDiagnosesSeverityCase =
    severityFilter &&
    'value' in severityFilter &&
    severityFilter.value.length === 2 &&
    severityFilter.value?.every(value => [ 3, 4 ].includes(value as number)) &&
    !filters?.some(filter => filter.name !== 'severity' && 'value' in filter && filter.value.length > 0)

  void debouncedQueryStatus

  let gridTemplateColumns = columns
    .map(column => column.gridArea ?? '1fr')
    .join(' ')

  let handleSortClick = useCallback((name: keyof T) => {
    if (!sort) {
      return
    }

    let column = columns.find(column => column.name === name)

    if (
      !column ||
      !column.isSortable
    ) {
      return
    }

    let direction: SortDirection = sort.field === column.name
      ? sort.direction === 'asc'
        ? 'desc'
        : 'asc'
      : column.initialSortDirection ?? 'asc'

    onSortChange(column.name, direction)
  }, [ columns, sort, onSortChange ])

  let handleColumnHeaderClick = useCallback((name: keyof T) => {
    if (!filters) {
      return
    }

    let column = columns.find(column => column.name === name)

    if (!column) {
      return
    }

    let filter = filters.find(filter => filter.name === name.toString(10))

    if (!filter) {
      return
    }

    if (currentlyOpenFilter === name) {
      setCurrentlyOpenFilter(undefined)
      return
    }

    setCurrentlyOpenFilter(name)
  }, [ columns, filters, currentlyOpenFilter ])

  useDebounce(() => {
    setDebouncedQueryStatus(queryStatus)
  }, queryStatus === 'pending' ? 0 : 100, [ queryStatus ])

  useDebounce(() => {
    setDebouncedFetchStatus(fetchStatus)
  }, fetchStatus === 'idle' ? 0 : 650, [ fetchStatus ])

  let hasExpandingComponents = !!renderExpandingComponent && data.some(item => !!renderExpandingComponent(item, expanded, setExpanded))

  if (hasExpandingComponents) {
    gridTemplateColumns = `32px ${gridTemplateColumns}`
  }

  return (
    <Card.Card expandable={typeof expandable === 'boolean' ? expandable : !!header}>
      {header && (
        <Card.Header hideBorders={hideBorders}>
          {header}
        </Card.Header>
      )}

      <Card.ExpandableWrapper>
        {typeof children !== 'undefined' && children}

        {typeof children === 'undefined' && (
          <div
            className={cx(styles.scrollableWrapper, [
              disableMinHeight && styles.disableMinHeight,
              !pagination && styles.rounded
            ])}
            style={{ ...wrapperStyle, display: 'flex', flexDirection: 'column' }}
          >
            <ContainerMeasureProvider>
              <LoadingIndicator
                loading={(debouncedQueryStatus === 'pending') || debouncedFetchStatus === 'fetching'}
                spinner={loadingIndicator}
                delay={0}
              />
              <table
                className={cx(styles.table, [
                  (!!error || isEmpty) && styles.noData
                ])}
                style={{
                  gridTemplateColumns
                }}
              >
                {(queryStatus !== 'pending' || fetchStatus === 'idle') && (
                  <thead>
                    {columns.map((column, i) => {
                      let name = column.name?.toString(10)
                      let filter = filters?.find(filter => filter.name === name)
                      let isFilterActive = false
                      let isFilterDisabled = false

                      if (filter && 'value' in filter) {
                        isFilterActive = filter && filter.value.length > 0
                        isFilterDisabled = filter && filter.value.length === 0 && filter.options.length === 0
                      }

                      if (filter && 'render' in filter) {
                        isFilterActive = filter.isActive ?? false
                        isFilterDisabled = filter.isDisabled ?? false
                      }

                      let Component: keyof JSX.IntrinsicElements = filter ? 'button' : 'div'
                      let props: React.ComponentPropsWithoutRef<'div'> & React.ComponentPropsWithoutRef<'button'> = {}

                      if (filter) {
                        props = {
                          type: 'button',
                          disabled: isFilterDisabled,
                          onClick: () => {
                            handleColumnHeaderClick(column.name)
                          }
                        }
                      }

                      return (
                        <Fragment key={`column-${column.name.toString()}-0`}>
                          {i === 0 && hasExpandingComponents && (
                            <th />
                          )}

                          <th style={column.headerStyle}>
                            <Component
                              className={styles.headerWrapper}
                              {...props}
                            >
                              <div className={styles.header}>
                                {column.header}
                              </div>

                              {filter && (
                                <div
                                  className={cx(styles.filtersIconWrapper, [
                                    isFilterActive && styles.isActive
                                  ])}
                                  title='Filter'
                                >
                                  <MdFilterAlt size='1rem' />
                                </div>
                              )}
                            </Component>

                            {column.isSortable && (
                              <button
                                type='button'
                                onClick={() => {
                                  handleSortClick(column.name)
                                }}
                                title={
                                  sort?.field === column.name
                                    ? sort.direction === 'desc'
                                      ? sortDirectionTooltipsMapper.asc
                                      : sortDirectionTooltipsMapper.desc
                                    : sortDirectionTooltipsMapper[column.initialSortDirection ?? 'asc']
                                }
                                className={cx(styles.sortButton, [
                                  sort?.field === column.name && styles.active,
                                  sort?.field === column.name && sort.direction === 'desc' && styles.desc,
                                  sort?.field !== column.name && column.initialSortDirection === 'desc' && styles.desc
                                ])}
                              >
                                <BarsIcon />
                              </button>
                            )}

                            {filter && 'value' in filter && currentlyOpenFilter === name && (
                              <FiltersDropdown
                                value={filter.value}
                                options={filter.options}
                                onChange={value => {
                                  onFilterChange?.(name, value)
                                }}
                                onClose={() => {
                                  setCurrentlyOpenFilter(undefined)
                                }}
                              />
                            )}

                            {filter && 'render' in filter && currentlyOpenFilter === name && (
                              <FiltersDropdownWrapper
                                onClose={() => {
                                  setCurrentlyOpenFilter(undefined)
                                }}
                              >
                                {filter.render({
                                  onChange (value: Array<string | number | boolean>) {
                                    onFilterChange?.(name, value)
                                  },
                                  onClose () {
                                    setCurrentlyOpenFilter(undefined)
                                  }
                                })}
                              </FiltersDropdownWrapper>
                            )}
                          </th>
                        </Fragment>
                      )
                    })}
                  </thead>
                )}

                <tbody>
                  {data.map((item, i, data) => {
                    let ExpandingComponent = renderExpandingComponent?.(item, expanded, setExpanded)

                    return (
                      <Fragment key={`row-${item.id.toString()}`}>
                        <tr
                          className={cx([
                            expanded === item.id && styles.expanded
                          ])}
                          onClick={() => {
                            const handled = onClick?.(item)

                            if (handled) return

                            if (window?.getSelection()?.toString().length) return

                            if (ExpandingComponent) {
                              setExpanded((prev) => prev === item.id ? null : item.id)
                            }
                          }}
                        >
                          {columns.map((column, j) => {
                            let key = `${item.id}-${column.name.toString()}`

                            let value: ReactNode = null

                            if (column.getValue) {
                              value = column.getValue?.(item, i)
                            }

                            if (!column.getValue && !value && (typeof item?.[column.name] === 'string' || typeof item?.[column.name] === 'number')) {
                              let v = item[column.name]

                              if (typeof v === 'string') {
                                value = v.trim() || null
                              }

                              if (typeof v === 'number') {
                                value = v
                              }
                            }

                            if (i > 0) {
                              let item = data[i - 1] as T
                              let previousValue = column.getValue?.(item, i) ?? item[column.name]

                              if (column.isMergeable && value === previousValue) {
                                return (
                                  <Fragment key={key}>
                                    {j === 0 && hasExpandingComponents && (
                                      <td />
                                    )}

                                    <td />
                                  </Fragment>
                                )
                              }
                            }

                            return (
                              <Fragment key={key}>
                                {j === 0 && !!ExpandingComponent && (
                                  <td
                                    style={{
                                      paddingLeft: 20,
                                      paddingRight: 6,
                                      display: 'flex',
                                      alignItems: 'flex-start',
                                      justifyContent: 'center'
                                    }}
                                  >
                                    <Chevron isOpen={expanded === item.id} />
                                  </td>
                                )}

                                {j === 0 && !ExpandingComponent && hasExpandingComponents && (
                                  <td />
                                )}

                                <td
                                  className={cx(value === null && styles.valueIsNull)}
                                  style={column.style}
                                >
                                  {value ?? '—'}
                                </td>
                              </Fragment>
                            )
                          })}
                        </tr>

                        {expanded === item.id && ExpandingComponent && (
                          <div
                            className={styles.expandableContent}
                            style={{ gridColumnEnd: `span ${columns.length + 1}` }}
                          >
                            {ExpandingComponent}
                          </div>
                        )}
                      </Fragment>
                    )
                  })}
                </tbody>
              </table>

              {error && (
                <ErrorPlaceHolder
                  title='Something went wrong'
                  message='Currently we are unable to load the data. Please try again later.'
                >
                  {typeof onRefetch === 'function' && (
                    <PlainButton
                      type='button'
                      color='primary'
                      btnType='contained'
                      label={debouncedFetchStatus === 'fetching' ? 'Retrying..' : 'Retry'}
                      disabled={debouncedFetchStatus === 'fetching'}
                      onClick={() => {
                        void onRefetch()
                      }}
                    />
                  )}
                </ErrorPlaceHolder>
              )}

              {!error && isEmpty && (
                <NoDataPlaceHolder
                  title={
                    noDataPlaceholder ?? (canClearFiltersForMoreResults
                      ? isSpecialDiagnosesSeverityCase
                        ? 'No claims exist for this patient with Severity Level of 3 or 4'
                        : 'No results match the search and filter criteria'
                      : 'This patient has no claims')
                  }
                >
                  {(canClearFiltersForMoreResults || forceClearFilters) && (
                    <PlainButton
                      type='button'
                      color='primary'
                      btnType='contained'
                      label={
                        debouncedFetchStatus === 'fetching'
                          ? 'Loading...'
                          : (canClearFiltersForMoreResults || forceClearFilters)
                            ? 'Clear filters'
                            : 'Reload'
                      }
                      disabled={debouncedFetchStatus === 'fetching'}
                      onClick={() => {
                        onFiltersClear?.()
                      }}
                    />
                  )}
                </NoDataPlaceHolder>
              )}
            </ContainerMeasureProvider>
          </div>
        )}
        {!error && !isEmpty && pagination && (
          <div className={styles.paginationContainer}>
            <PaginationComponent
              {...pagination}
              total={pagination.totalFiltered}
              onPageChange={onPageChange}
              onPageSizeChange={onPageSizeChange}
            />
          </div>
        )}
      </Card.ExpandableWrapper>
    </Card.Card>
  )
}

interface ChevronProps {
  isOpen: boolean
}

function Chevron ({ isOpen }: ChevronProps): JSX.Element {
  let style = useSpring({
    to: {
      transform: `rotate(${isOpen ? 90 : 0}deg)`
    },
    config: config.wobbly
  })

  return (
    <animated.div
      style={{
        flexShrink: 0,
        flexGrow: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...style
      }}
    >
      <MdChevronRight
        size={20}
      />
    </animated.div>
  )
}
